import { Link, useNavigate } from "react-router-dom";
import { Crown, Zap, Coins, LogOut, Mail, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const isPro = ["pro", "creator_plus"].includes(user.plan);
  const planLabel = user.plan_label || (user.plan === "creator_plus" ? "Creator Plus" : isPro ? "Pro" : "Free");
  const dailyCap = user.daily_cap || 1;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
      <p className="eyebrow">Account</p>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">Your plan & usage</h1>

      <div className="mt-8 rounded-3xl border border-white/10 bg-[#11141E] p-7" data-testid="account-card">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
          ) : (
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FF453A]/20 text-[#FF6A5E] font-display font-bold text-xl">
              {(user.name || user.email)[0]?.toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="font-display font-bold text-lg truncate" data-testid="account-name">{user.name || "—"}</p>
            <p className="text-sm text-slate-400 flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5" /> {user.email}</p>
          </div>
        </div>

        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          <div className={`rounded-2xl border p-5 ${isPro ? "border-[#FF453A]/40 bg-[#FF453A]/[0.06]" : "border-white/10 bg-[#0B0C10]"}`} data-testid="account-plan">
            <div className="flex items-center gap-2">
              {isPro ? <Crown className="h-5 w-5 text-[#FF6A5E]" /> : <Zap className="h-5 w-5 text-slate-300" />}
              <span className="font-semibold">{planLabel} plan</span>
              {isPro && <BadgeCheck className="h-4 w-4 text-[#FF6A5E]" />}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              {isPro ? `${dailyCap === 1 ? "1 video" : `${dailyCap} videos`} every day + priority rendering.` : "1 free video per week."}
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-5" data-testid="account-credits">
            <div className="flex items-center gap-2"><Coins className="h-5 w-5 text-amber-400" /><span className="font-semibold">Credits</span></div>
            <p className="mt-2 font-display font-extrabold text-2xl">{user.credits ?? 0}</p>
            <p className="text-xs text-slate-500">Used after your daily free videos.</p>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          {!isPro && (
            <Link to="/pricing">
              <Button className="rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white font-semibold px-6" data-testid="account-upgrade-btn">
                <Crown className="h-4 w-4 mr-2" /> Upgrade / buy credits
              </Button>
            </Link>
          )}
          {isPro && (
            <Link to="/pricing"><Button variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5" data-testid="account-manage-btn">Buy more credits</Button></Link>
          )}
          <Button onClick={doLogout} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white" data-testid="account-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
